/* eslint-disable import/prefer-default-export */
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import CodeIcon from "@mui/icons-material/Code";
import FunctionsIcon from "@mui/icons-material/Functions";
import TableChartIcon from "@mui/icons-material/TableChart";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { GridColDef } from "@mui/x-data-grid";
import dayjs from "dayjs";

import {
  getDaysSinceUsage,
  getObjectTypeColor,
  getUsageColor,
} from "./unused-sql-objects-utils";

const getObjectTypeIcon = (objectType: string) => {
  const type = objectType.toLowerCase();
  if (type.includes("table")) return <TableChartIcon fontSize="small" />;
  if (type.includes("view")) return <ViewModuleIcon fontSize="small" />;
  if (type.includes("procedure") || type.includes("function"))
    return <FunctionsIcon fontSize="small" />;
  return <CodeIcon fontSize="small" />;
};

export const getUnusedObjectsColumns = (): GridColDef[] => [
  {
    field: "objectName",
    headerName: "Object Name",
    flex: 2,
    minWidth: 200,
    renderCell: (params) => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          height: "100%",
        }}
      >
        {getObjectTypeIcon(params.row.objectType)}
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      </Box>
    ),
  },
  {
    field: "schemaName",
    headerName: "Schema",
    flex: 1,
    minWidth: 120,
    renderCell: (params) => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Typography variant="body2">{params.value}</Typography>
      </Box>
    ),
  },
  {
    field: "objectType",
    headerName: "Type",
    flex: 1,
    minWidth: 120,
    renderCell: (params) => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Chip
          label={params.value}
          size="small"
          color={getObjectTypeColor(params.value)}
          sx={{ fontWeight: 500 }}
        />
      </Box>
    ),
  },
  {
    field: "lastObservedUsageDate",
    headerName: "Last Used",
    flex: 1,
    minWidth: 150,
    renderCell: (params) => {
      if (!params.value) {
        return (
          <Tooltip title="No usage data available">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "text.disabled",
                  fontStyle: "italic",
                }}
              >
                Not known
              </Typography>
            </Box>
          </Tooltip>
        );
      }
      const daysAgo = getDaysSinceUsage(params.value);
      const dateObj = new Date(params.value);
      return (
        <Tooltip title={dateObj.toLocaleString()}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: getUsageColor(daysAgo),
                fontWeight: daysAgo > 365 ? 600 : 400,
              }}
            >
              {dayjs(dateObj).fromNow()}
            </Typography>
          </Box>
        </Tooltip>
      );
    },
  },
  {
    field: "referencedObjectCount",
    headerName: "References",
    flex: 0.8,
    minWidth: 100,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          width: "100%",
        }}
      >
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          color={params.value === 0 ? "default" : "primary"}
        />
      </Box>
    ),
  },
  {
    field: "referencingObjectCount",
    headerName: "Referenced By",
    flex: 0.8,
    minWidth: 120,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          width: "100%",
        }}
      >
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          color={params.value === 0 ? "error" : "success"}
        />
      </Box>
    ),
  },
  {
    field: "reason",
    headerName: "Reason",
    flex: 2.5,
    minWidth: 200,
    renderCell: (params) => (
      <Box
        sx={{
          py: 1,
          display: "flex",
          alignItems: "flex-start",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            whiteSpace: "normal",
            wordWrap: "break-word",
            lineHeight: 1.5,
          }}
        >
          {params.value}
        </Typography>
      </Box>
    ),
  },
];
